// Generic CRUD-style list page. Each resource declares its columns + filters
// and a fetcher; the component handles loading, pagination, search, error
// surfacing, and action wiring. Cuts the 7 list pages from ~150 LOC each to
// ~40 LOC of schema config.

import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Space,
  Button,
  Card,
  Typography,
  message,
  Form,
  DatePicker,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { ListResult } from '@/api/client';

type FilterDef =
  | { key: string; label: string; type: 'text'; placeholder?: string }
  | {
      key: string;
      label: string;
      type: 'select';
      options: Array<{ value: string; label: string }>;
      allowClear?: boolean;
    }
  | { key: string; label: string; type: 'daterange' };

export interface ResourceListProps<T> {
  /** Page heading. */
  title: string;
  /** Inline tooltip / description below the heading. */
  description?: React.ReactNode;
  /** Columns for antd Table — all standard column props are accepted. */
  columns: ColumnsType<T>;
  /** Filter inputs rendered above the table. */
  filters?: FilterDef[];
  /** Server query — receives `{ filters, page, pageSize, cursor }`. */
  fetcher: (args: {
    filters: Record<string, unknown>;
    page: number;
    pageSize: number;
    cursor?: string;
  }) => Promise<ListResult<T>>;
  /** Stable row identifier. */
  rowKey: keyof T | ((row: T) => string | number);
  /** Pagination mode — offset (page/page_size) or cursor (next token). */
  pagination?: 'offset' | 'cursor';
  /** Actions rendered top-right (e.g. "新建"). */
  actions?: Array<{
    label: string;
    onClick: () => void;
    type?: 'primary' | 'default';
    icon?: React.ReactNode;
    /** Show a spinner (e.g. rate-limited sync in flight). */
    loading?: boolean;
    /** Disable the button (e.g. block re-click during rate-limited sync). */
    disabled?: boolean;
  }>;
  /** Default page size. */
  defaultPageSize?: number;
}

/** Build query params from filter state, dropping empty values + flattening date ranges. */
function buildFilterParams(state: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(state)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 2 && v[0] && v[1]) {
      // dayjs range → from/to strings
      const [from, to] = v as [{ toISOString?: () => string }, { toISOString?: () => string }];
      const fromStr = typeof from?.toISOString === 'function' ? from.toISOString() : String(from);
      const toStr = typeof to?.toISOString === 'function' ? to.toISOString() : String(to);
      out[`${k}_from`] = fromStr;
      out[`${k}_to`] = toStr;
      continue;
    }
    out[k] = v;
  }
  return out;
}

export function ResourceList<T extends object>(props: ResourceListProps<T>) {
  const {
    title,
    description,
    columns,
    filters = [],
    fetcher,
    rowKey,
    pagination = 'offset',
    actions = [],
    defaultPageSize = 20,
  } = props;

  const [form] = Form.useForm();
  const [filterState, setFilterState] = useState<Record<string, unknown>>({});
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const load = async (
    nextPage = page,
    nextSize = pageSize,
    nextFilters = filterState,
    nextCursorArg: string | undefined = pagination === 'cursor' ? cursor : undefined,
  ) => {
    setLoading(true);
    try {
      const res = await fetcher({
        filters: buildFilterParams(nextFilters),
        page: nextPage,
        pageSize: nextSize,
        cursor: nextCursorArg,
      });
      setData(res.data);
      if (res.page) setTotal(res.page.total);
      if (res.cursor) setNextCursor(res.cursor.next);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '加载失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1, pageSize, filterState, undefined);
    // Only run on mount — filter changes go through the explicit "查询" button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPage(1);
    setCursor(undefined);
    void load(1, pageSize, filterState, undefined);
  };

  const handleReset = () => {
    form.resetFields();
    const empty: Record<string, unknown> = {};
    setFilterState(empty);
    setPage(1);
    setCursor(undefined);
    void load(1, pageSize, empty, undefined);
  };

  const handleTableChange = (pag: TablePaginationConfig) => {
    if (pagination !== 'offset') return;
    const p = pag.current ?? 1;
    const s = pag.pageSize ?? pageSize;
    setPage(p);
    setPageSize(s);
    void load(p, s, filterState, undefined);
  };

  const filterControls = useMemo(
    () =>
      filters.map((f) => {
        switch (f.type) {
          case 'text':
            return (
              <Form.Item name={f.key} label={f.label} key={f.key} style={{ marginBottom: 0 }}>
                <Input
                  allowClear
                  placeholder={f.placeholder || `按 ${f.label} 搜索`}
                  style={{ width: 200 }}
                  onPressEnter={handleSearch}
                />
              </Form.Item>
            );
          case 'select':
            return (
              <Form.Item name={f.key} label={f.label} key={f.key} style={{ marginBottom: 0 }}>
                <Select
                  allowClear={f.allowClear !== false}
                  options={f.options}
                  style={{ width: 160 }}
                  placeholder={`选择 ${f.label}`}
                />
              </Form.Item>
            );
          case 'daterange':
            return (
              <Form.Item name={f.key} label={f.label} key={f.key} style={{ marginBottom: 0 }}>
                <DatePicker.RangePicker showTime />
              </Form.Item>
            );
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          {description && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
              {description}
            </Typography.Paragraph>
          )}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load(page, pageSize, filterState, cursor)}>
            刷新
          </Button>
          {actions.map((a) => (
            <Button
              key={a.label}
              type={a.type ?? 'primary'}
              icon={a.icon}
              onClick={a.onClick}
              loading={a.loading}
              disabled={a.disabled}
            >
              {a.label}
            </Button>
          ))}
        </Space>
      </div>

      {filters.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="inline"
            onValuesChange={(_, all) => setFilterState(all)}
          >
            <Space wrap size={[12, 12]}>
              {filterControls}
              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                    查询
                  </Button>
                  <Button onClick={handleReset}>重置</Button>
                </Space>
              </Form.Item>
            </Space>
          </Form>
        </Card>
      )}

      <Table<T>
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={rowKey as any}
        size="middle"
        scroll={{ x: 'max-content' }}
        pagination={
          pagination === 'offset'
            ? {
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 条`,
              }
            : false
        }
        onChange={handleTableChange}
      />

      {pagination === 'cursor' && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Typography.Text type="secondary">{`已加载 ${data.length} 条`}</Typography.Text>
            <Button
              disabled={!nextCursor}
              onClick={() => {
                setCursor(nextCursor);
                void load(page, pageSize, filterState, nextCursor);
              }}
            >
              下一页
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
}
